import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

/**
 * NumberField — touch-friendly numeric input.
 *
 * Designed to replace `<Input type="number" value={n} onChange={parseFloat}>`
 * in the quote / contract / timeline modules. The native `type=number` input
 * has three UX problems on iPad / desktop that hurt salon use:
 *
 *   1. When the model value is 0 the field shows "0" — the user has to
 *      manually select-all before typing "55", or risk producing "055".
 *   2. Native browser spinners (▲▼) are ugly and easy to mis-tap.
 *   3. iOS shows the wrong keyboard variant on certain selectors.
 *
 * This component fixes all three:
 *
 *   • `type="text"` + `inputMode="decimal"` → iPad/iPhone show the numeric
 *     keypad, desktop shows no spinners.
 *   • Internal string state is kept in sync with the parent's number, but
 *     a numeric value of 0 is displayed as **empty** so a fresh tap leads
 *     straight to typing.
 *   • Italian decimal separator (",") is accepted and normalized to "."
 *     before emitting to the parent.
 *   • Leading-zero stripping ("055" → "55") on blur, but NOT during input
 *     so the user can still type "0.5".
 *
 * The parent always receives a plain Number through `onChange`. An empty
 * field emits 0 — the same contract the legacy `parseFloat(e.target.value) || 0`
 * had — so callers don't need to special-case undefined.
 */

/**
 * Format a number for display. Returns "" when the number is exactly 0
 * (the model's "empty" sentinel) so the placeholder shows. Other zero
 * values (typed by the user) are kept as the user typed them.
 */
function numToDisplay(n) {
  if (n === null || n === undefined) return "";
  const num = Number(n);
  if (!Number.isFinite(num) || num === 0) return "";
  // Strip trailing ".0" from integers so "5" stays "5", not "5.0"
  return Number.isInteger(num) ? String(num) : String(num);
}

/** Parse a user-typed string back to a Number. Empty / unparseable → 0. */
function displayToNum(s) {
  if (s === null || s === undefined) return 0;
  const cleaned = String(s).trim().replace(",", ".");
  if (!cleaned) return 0;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

/** Reject characters that would never produce a valid number. We accept a
 *  single leading `-`, digits, and one decimal separator (`,` or `.`). */
function sanitizeKeystroke(raw, { allowNegative, allowDecimals }) {
  if (!raw) return "";
  let s = String(raw);
  // Allow only digits, '.', ',', and a leading '-'
  s = s.replace(/[^\d.,-]/g, "");
  // Collapse multiple '-' and only keep it as a leading char
  if (allowNegative) {
    const hasLeadingMinus = s.startsWith("-");
    s = s.replace(/-/g, "");
    if (hasLeadingMinus) s = "-" + s;
  } else {
    s = s.replace(/-/g, "");
  }
  // Collapse any decimal separator runs to a single one (the first)
  if (allowDecimals) {
    let firstSep = -1;
    let out = "";
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === "." || ch === ",") {
        if (firstSep === -1) {
          firstSep = i;
          out += ch;
        }
        // else: drop additional separators silently
      } else {
        out += ch;
      }
    }
    s = out;
  } else {
    s = s.replace(/[.,]/g, "");
  }
  return s;
}

export const NumberField = React.forwardRef(function NumberField(
  {
    value,
    onChange,
    placeholder = "0",
    allowDecimals = true,
    allowNegative = false,
    min,
    max,
    className = "",
    ...rest
  },
  ref,
) {
  // Local string mirror. Synced from the parent's `value` whenever the
  // parent changes it externally (e.g. after a save round-trip), but NOT
  // overwritten while the input is focused — otherwise the user would lose
  // an in-progress "0.5" the moment the parent reflows to "0.5".
  const [display, setDisplay] = useState(() => numToDisplay(value));
  const focused = useRef(false);

  useEffect(() => {
    if (focused.current) return;
    setDisplay(numToDisplay(value));
  }, [value]);

  const emit = (s) => {
    let n = displayToNum(s);
    if (typeof min === "number" && Number.isFinite(min) && n < min) n = min;
    if (typeof max === "number" && Number.isFinite(max) && n > max) n = max;
    if (typeof onChange === "function") onChange(n);
  };

  const handleChange = (e) => {
    const next = sanitizeKeystroke(e.target.value, { allowNegative, allowDecimals });
    setDisplay(next);
    emit(next);
  };

  const handleBlur = () => {
    focused.current = false;
    // Normalize on blur: clamp + strip leading zeros + collapse "," → "."
    let s = display.replace(",", ".");
    if (s === "" || s === "-" || s === ".") {
      setDisplay("");
      emit("");
      return;
    }
    const n = displayToNum(s);
    let clamped = n;
    if (typeof min === "number" && Number.isFinite(min) && clamped < min) clamped = min;
    if (typeof max === "number" && Number.isFinite(max) && clamped > max) clamped = max;
    setDisplay(numToDisplay(clamped));
    if (clamped !== n) emit(String(clamped));
  };

  const handleFocus = () => { focused.current = true; };

  return (
    <Input
      ref={ref}
      type="text"
      // `decimal` shows the iOS / Android numeric keypad with a "." key;
      // when allowDecimals is false we fall back to "numeric" which hides
      // it entirely.
      inputMode={allowDecimals ? "decimal" : "numeric"}
      // `pattern` reinforces native validation on browsers that honor it.
      pattern={allowDecimals ? "[0-9]*[.,]?[0-9]*" : "[0-9]*"}
      autoComplete="off"
      autoCorrect="off"
      spellCheck={false}
      placeholder={placeholder}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
      {...rest}
    />
  );
});

export default NumberField;
